// A week with nothing planned (wireframe 14): three ways to start.
// "Start from last week" copies it, leftovers links and dining out
// included; "Start from another plan" offers your other weeks and the
// saved/public plans; "Start blank" shows the empty week to fill a day at a
// time.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { COLORS, FONT_SIZE, RADIUS, SPACE } from "../../lib/constants";
import { orderedDays, shiftWeek } from "../../lib/utils";
import { weekMealCount } from "../../lib/planUtils";
import { weekRange, weekSummary } from "../../lib/planView";
import type { Day } from "../../lib/types";
import { supporting } from "../recipeStyles";
import { textButton } from "./planStyles";

const card = {
  display: "flex",
  flexDirection: "column" as const,
  gap: SPACE.xs,
  width: "100%",
  textAlign: "left" as const,
  border: `1px solid ${COLORS.line}`,
  borderRadius: RADIUS.xl,
  background: COLORS.card,
  padding: "var(--space-xl) var(--space-xxl)",
  cursor: "pointer",
  color: COLORS.ink,
  fontFamily: "var(--font-sans)",
  // Buttons don't inherit the page's font size.
  fontSize: FONT_SIZE.base,
  textDecoration: "none",
};

export function EmptyWeek({ onStartBlank }: { onStartBlank: () => void }) {
  const mp = useMealPlanner();
  const [showOthers, setShowOthers] = useState(false);
  const days = orderedDays(mp.planDefaults.weekStartDay) as Day[];
  const lastWeek = shiftWeek(mp.weekStart, -1);
  const lastWeekPlan = mp.plansByWeek[lastWeek];
  const others = Object.keys(mp.plansByWeek)
    .filter((k) => k !== mp.weekStart && k !== lastWeek && weekMealCount(mp.plansByWeek[k]) > 0)
    .sort((a, b) => (a < b ? 1 : -1));

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <div>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h2, fontWeight: 600 }}>This week is empty</div>
        <div style={supporting}>How do you want to start?</div>
      </div>

      {weekMealCount(lastWeekPlan) > 0 && (
        <button type="button" onClick={() => mp.applyWeekAsTemplate(lastWeek)} style={{ ...card, borderColor: COLORS.accent }}>
          <strong style={{ color: COLORS.accent }}>Start from last week</strong>
          <span style={supporting}>
            {weekRange(lastWeek)} · {weekSummary(lastWeekPlan, days)}
          </span>
          <span style={{ fontSize: FONT_SIZE.sm }}>Copies the meals. Leftovers and dining out come along; you change what you like.</span>
        </button>
      )}

      <div style={{ ...card, cursor: "default" }}>
        <button type="button" aria-expanded={showOthers} onClick={() => setShowOthers((s) => !s)} style={{ ...textButton, color: COLORS.ink, flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
          <strong style={{ color: COLORS.accent }}>Start from another plan</strong>
          <span style={{ ...supporting, fontWeight: 400 }}>An earlier week of yours, one of your saved plans, or a public plan.</span>
        </button>
        {showOthers && (
          <div style={{ marginTop: SPACE.md }}>
            {others.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => mp.applyWeekAsTemplate(k)}
                style={{ ...textButton, color: COLORS.ink, fontWeight: 400, width: "100%", justifyContent: "space-between", borderTop: `1px solid ${COLORS.line}`, textAlign: "left" }}
              >
                <span>
                  <strong>{mp.plansByWeek[k].title || weekRange(k)}</strong>
                  <span style={{ ...supporting, display: "block" }}>{weekSummary(mp.plansByWeek[k], days)}</span>
                </span>
                <span style={{ color: COLORS.accent }}>Use</span>
              </button>
            ))}
            <Link to="/plan/public" style={{ ...textButton, borderTop: `1px solid ${COLORS.line}`, width: "100%" }}>
              Saved and public plans ›
            </Link>
          </div>
        )}
      </div>

      <button type="button" onClick={onStartBlank} style={card}>
        <strong style={{ color: COLORS.accent }}>Start blank</strong>
        <span style={supporting}>Seven empty days. Add dinners one at a time.</span>
      </button>
    </div>
  );
}
